import { FC, KeyboardEvent, MouseEvent, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Box,
  ButtonBase,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Tooltip,
} from '@mui/material';
import { FormatColorReset, KeyboardArrowDown } from '@mui/icons-material';
import { Editor, useEditorState } from '@tiptap/react';
import { formatTranslation } from 'intl';
import { RichTextTranslationKeys } from 'intl/keys';
import { normalizeTextColor } from 'shared/utils/richText';
import { useCallOncePerAnimationFrame } from 'hooks/useCallOncePerAnimationFrame';
import { RICH_TEXT_POPUP_ATTRIBUTE } from '../richTextPopup';
import { toolbarIconSx } from '../styles';
import { readSelectionTextColor } from './model';
import { DEFAULT_TEXT_COLOR } from './palette';
import { TextColorMenu } from './TextColorMenu';
import { useTextColorHistory } from './useTextColorHistory';
import {
  colorLetterSx,
  menuArrowIconSx,
  pickerPaperSx,
  pickerPopperSx,
  splitButtonApplySx,
  splitButtonMenuSx,
  splitButtonSx,
} from './styles';

interface TextColorButtonsProps {
  editor: Editor;
  disabled?: boolean;
}

/** Above the toolbar, so the menu does not cover the text; below it only when there is no room above. */
const MENU_POPPER_MODIFIERS = [
  { name: 'flip', options: { fallbackPlacements: ['bottom-start'] } },
  { name: 'offset', options: { offset: [0, 8] } },
];

/**
 * Text colour of the selection (PT-7250), as in word processors: the "A"
 * applies the last colour used in one click, the arrow opens the menu with
 * the basic and recent colours and any other one, and a button next to them
 * removes the colour.
 */
export const TextColorButtons: FC<TextColorButtonsProps> = ({
  editor,
  disabled,
}) => {
  const intl = useIntl();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { used, remember, reload } = useTextColorHistory();
  // A custom colour is applied while it is chosen and remembered once, when the menu closes.
  const pendingCustomColorRef = useRef<string | null>(null);
  // Re-read on every selection change: the editor does not re-render the toolbar for it.
  const selectionColor = useEditorState({
    editor,
    selector: ({ editor: current }) => readSelectionTextColor(current),
  });
  const [lastUsedColor = DEFAULT_TEXT_COLOR] = used;

  const applyColor = (value: string): string | undefined => {
    const color = normalizeTextColor(value);
    if (color) {
      editor.chain().setColor(color).run();
    }
    return color;
  };
  // Dragging across the picker fires on every pointer move; one change per frame is what shows.
  const applyCustomColor = useCallOncePerAnimationFrame(
    (value: string) => {
      pendingCustomColorRef.current =
        applyColor(value) ?? pendingCustomColorRef.current;
    }
  );

  const closeMenu = (): void => {
    setMenuAnchor(null);
    // The last dragged colour may still wait for its frame: it is applied and remembered too.
    applyCustomColor.flush();
    if (pendingCustomColorRef.current) {
      remember(pendingCustomColorRef.current);
      pendingCustomColorRef.current = null;
    }
  };

  /** Back to the text, where the user types on in the colour. */
  const closeMenuToText = (): void => {
    closeMenu();
    editor.commands.focus();
  };

  const applyLastUsedColor = (): void => {
    applyColor(lastUsedColor);
    remember(lastUsedColor);
    editor.commands.focus();
  };

  const pickColor = (color: string): void => {
    // The swatch replaces a colour dragged before it: that one is neither applied nor remembered.
    applyCustomColor.cancel();
    pendingCustomColorRef.current = null;
    applyColor(color);
    remember(color);
    closeMenuToText();
  };

  const toggleMenu = (event: MouseEvent<HTMLElement>): void => {
    if (menuAnchor) {
      closeMenu();
      return;
    }
    reload();
    setMenuAnchor(event.currentTarget);
  };

  /** A click elsewhere closes the menu and leaves the focus where the user clicked. */
  const closeMenuOnClickAway = (
    event: globalThis.MouseEvent | TouchEvent
  ): void => {
    // The arrow toggles the menu itself.
    if (
      event.target instanceof Node &&
      menuAnchor?.contains(event.target)
    ) {
      return;
    }
    closeMenu();
  };

  /** Escape goes back to the text; a dialog around stays open. */
  const closeMenuOnEscape = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== 'Escape') {
      return;
    }
    event.stopPropagation();
    closeMenuToText();
  };

  return (
    <>
      {/* One pill: the "A" in the last colour applies it, the arrow opens the menu. */}
      <Box sx={splitButtonSx(disabled)}>
        <Tooltip
          title={`${formatTranslation(intl, RichTextTranslationKeys.richtextTextColor)} (${lastUsedColor})`}
        >
          <span>
            <ButtonBase
              onClick={applyLastUsedColor}
              disabled={disabled}
              aria-label={formatTranslation(
                intl,
                RichTextTranslationKeys.richtextTextColor
              )}
              sx={splitButtonApplySx}
              data-qa='rich-text-text-color'
              data-color={lastUsedColor}
            >
              <Box component='span' sx={colorLetterSx(lastUsedColor)}>
                A
              </Box>
            </ButtonBase>
          </span>
        </Tooltip>
        <Tooltip
          title={formatTranslation(
            intl,
            RichTextTranslationKeys.richtextTextColorMenu
          )}
        >
          <span>
            <ButtonBase
              onClick={toggleMenu}
              disabled={disabled}
              aria-label={formatTranslation(
                intl,
                RichTextTranslationKeys.richtextTextColorMenu
              )}
              aria-haspopup='true'
              aria-expanded={Boolean(menuAnchor)}
              sx={splitButtonMenuSx(Boolean(menuAnchor))}
              data-qa='rich-text-text-color-menu'
            >
              <KeyboardArrowDown sx={menuArrowIconSx} />
            </ButtonBase>
          </span>
        </Tooltip>
        <Popper
          open={Boolean(menuAnchor) && !disabled}
          anchorEl={menuAnchor}
          placement='top-start'
          modifiers={MENU_POPPER_MODIFIERS}
          sx={pickerPopperSx}
          {...{ [RICH_TEXT_POPUP_ATTRIBUTE]: 'true' }}
        >
          <ClickAwayListener onClickAway={closeMenuOnClickAway}>
            <Paper
              elevation={8}
              sx={pickerPaperSx}
              onKeyDown={closeMenuOnEscape}
              data-qa='rich-text-color-picker'
            >
              <TextColorMenu
                selectionColor={selectionColor}
                usedColors={used}
                onPick={pickColor}
                onCustomColor={applyCustomColor.call}
              />
            </Paper>
          </ClickAwayListener>
        </Popper>
      </Box>
      <Tooltip
        title={formatTranslation(
          intl,
          RichTextTranslationKeys.richtextRemoveTextColor
        )}
      >
        <span>
          <IconButton
            onClick={() => editor.chain().focus().unsetColor().run()}
            disabled={disabled || !selectionColor}
            size='small'
            data-qa='rich-text-remove-text-color'
          >
            <FormatColorReset sx={toolbarIconSx} />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );
};
